import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showNotification } from '@mantine/notifications';
import {
  RefreshCw,
  Users,
  DollarSign,
  ClipboardList,
  Percent,
  LayoutDashboard,
  Store,
  Activity,
  ShieldCheck,
  Star,
  ChevronRight,
  AlertCircle,
  Zap,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { analyticsService } from '../../services/analyticsService';
import VendorIntelligenceDetail from '../../components/MasterAdmin/VendorIntelligenceDetail';
import InsightsPageShell from '../../components/MasterAdmin/insights/InsightsPageShell';
import InsightsStatCard from '../../components/MasterAdmin/insights/InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from '../../components/MasterAdmin/insights/InsightsChartCard';
import {
  opsFilterClass,
  opsPrimaryBtn,
  opsSecondaryBtn,
  opsChartCard,
  opsTableWrap,
  opsMetricGridClass,
  CHART_COLORS,
  PIE_PALETTE,
} from '../../components/MasterAdmin/operations/operationsUi';
import { monitoringMetricInfo } from '../../config/masterAdminMetricDefinitions';
import MetricInfoPopover from '../../components/MasterAdmin/ui/MetricInfoPopover';

const TABS = [
  { value: 'overview', label: 'Overview', icon: LayoutDashboard },
  { value: 'operations', label: 'Operations', icon: Store },
  { value: 'technical', label: 'Technical Tower', icon: Activity },
  { value: 'intelligence', label: 'Intelligence Detail', icon: Zap },
] as const;

const TIME_RANGES = [
  { value: '7', label: 'Week' },
  { value: '30', label: 'Month' },
  { value: '365', label: 'Year' },
];

function pctChange(value: number, prevValue: number): number {
  return prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
}

function pctChangeLabel(pct: number): string {
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs prior period`;
}

const alertCardClass =
  'border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-900/10';

const MonitoringPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState<string>('30');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get('category') || 'ambulance');
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [summaryTotals, setSummaryTotals] = useState<any>(null);
  const [healthData, setHealthData] = useState<any[]>([]);
  const [vendorData, setVendorData] = useState<any[]>([]);
  const [serviceData, setServiceData] = useState<any[]>([]);
  const [activeVendor, setActiveVendor] = useState<string | null>(null);
  const [allAmbulanceVendors, setAllAmbulanceVendors] = useState<any[]>([]);
  const [selectedIntelligenceVendor, setSelectedIntelligenceVendor] = useState<string | null>(
    searchParams.get('vendorId') || null
  );
  const [fetchingVendors, setFetchingVendors] = useState(false);

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    setSearchParams((prev) => {
      if (val) {
        prev.set('tab', val);
      } else {
        prev.delete('tab');
      }
      return prev;
    });
  };

  const handleCategoryChange = (val: string | null) => {
    setSelectedCategory(val);
    setSearchParams((prev) => {
      if (val) {
        prev.set('category', val);
      } else {
        prev.delete('category');
      }
      return prev;
    });
  };

  const handleVendorChange = (val: string | null) => {
    setSelectedIntelligenceVendor(val);
    setSearchParams((prev) => {
      if (val) {
        prev.set('vendorId', val);
      } else {
        prev.delete('vendorId');
      }
      return prev;
    });
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
    const cat = searchParams.get('category');
    if (cat && cat !== selectedCategory) {
      setSelectedCategory(cat);
    }
    const vId = searchParams.get('vendorId');
    if (vId && vId !== selectedIntelligenceVendor) {
      setSelectedIntelligenceVendor(vId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const getVendorDetailsUrl = (vendor: any) => {
    const cat = (vendor.category || selectedCategory || '').toLowerCase();
    if (cat === 'ambulance') {
      return `/vendors/ambulance/${vendor.vendor_id}/details`;
    } else if (cat === 'doctor' || cat === 'clinic') {
      return `/vendors/doctor-clinic/${vendor.vendor_id}/details`;
    } else if (cat === 'medical_store' || cat === 'medicalstore') {
      return `/vendors/medical-store/${vendor.vendor_id}/details`;
    }
    return null;
  };

  // Business summary + technical health + service mix only depend on the selected time range —
  // fetched independently from vendor intelligence so switching category doesn't refetch them.
  const fetchOverview = useCallback(async (days: number) => {
    try {
      setOverviewLoading(true);
      const [summary, health, services] = await Promise.all([
        analyticsService.getBusinessSummary(days),
        analyticsService.getTechnicalHealth(days),
        analyticsService.getServiceIntelligence(10),
      ]);
      setSummaryData(summary.data || []);
      setSummaryTotals(summary.summary_totals || null);
      const formattedHealth = (health.data || []).map((row: any) => ({
        ...row,
        avg_error_rate: (row.avg_error_rate || 0) * 100,
      }));
      setHealthData(formattedHealth);
      setServiceData(services.data || []);
    } catch (error) {
      console.error('Failed to fetch monitoring overview:', error);
      showNotification({
        title: 'Data Sync Failed',
        message: 'Could not load the latest monitoring data.',
        color: 'red',
      });
    } finally {
      setOverviewLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Vendor intelligence board is keyed off category only, so it can refresh without
  // re-fetching business/technical KPIs when the operator just changes the vertical.
  const fetchVendors = useCallback(async (category: string | null) => {
    try {
      setVendorsLoading(true);
      const vendors = await analyticsService.getVendorIntelligence(15, category);
      setVendorData(vendors.data || []);
    } catch (error) {
      console.error('Failed to fetch vendor intelligence:', error);
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview(parseInt(timeRange));
  }, [timeRange, fetchOverview]);

  useEffect(() => {
    fetchVendors(selectedCategory);
  }, [selectedCategory, fetchVendors]);

  useEffect(() => {
    const fetchVendorsList = async () => {
      try {
        setFetchingVendors(true);
        const cat = selectedCategory || 'ambulance';
        const vendors = await analyticsService.getVendorIntelligence(100, cat);
        setAllAmbulanceVendors(vendors.data || []);
      } catch (error) {
        console.error('Failed to fetch vendors list:', error);
      } finally {
        setFetchingVendors(false);
      }
    };

    if (activeTab === 'intelligence') {
      fetchVendorsList();
    }
  }, [activeTab, selectedCategory]);

  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);
      const days = parseInt(timeRange);
      await analyticsService.refreshAnalytics(days);
      showNotification({
        title: 'Refresh Started',
        message: `Recalculating the last ${days} days of metrics. Charts will update in 30-60 seconds.`,
        color: 'blue',
        autoClose: 10000,
      });
      setTimeout(() => {
        fetchOverview(days);
        fetchVendors(selectedCategory);
      }, 8000);
    } catch (error) {
      showNotification({ title: 'Refresh Failed', message: 'Connection to the analytics service failed.', color: 'red' });
      setRefreshing(false);
    }
  };

  const totalRevenue = summaryTotals?.total_revenue ?? 0;
  const totalOrders = summaryTotals?.total_orders ?? 0;
  const totalGMV = summaryTotals?.total_gmv ?? 0;
  const prevRevenue = summaryTotals?.prev_revenue ?? 0;
  const prevOrders = summaryTotals?.prev_orders ?? 0;
  const prevGMV = summaryTotals?.prev_gmv ?? 0;

  const currentMargin = totalGMV > 0 ? (totalRevenue / totalGMV) * 100 : 0;
  const prevMargin = prevGMV > 0 ? (prevRevenue / prevGMV) * 100 : 0;
  const marginChange = currentMargin - prevMargin;

  const maxActiveUsers = summaryTotals?.active_users ?? 0;
  const currentTotalUsers = summaryTotals?.total_members ?? 0;

  const timeLabel = timeRange === '7' ? 'Last Week' : timeRange === '30' ? 'Last Month' : 'Last Year';

  const revenueTrendPct = useMemo(() => pctChange(totalRevenue, prevRevenue), [totalRevenue, prevRevenue]);
  const ordersTrendPct = useMemo(() => pctChange(totalOrders, prevOrders), [totalOrders, prevOrders]);

  const pieServiceData = useMemo(
    () =>
      serviceData.map((s) => ({
        name:
          s.service === 'Authentication'
            ? 'Logins/Security'
            : s.service === 'App'
              ? 'App/Bot Traffic'
              : String(s.service || '').replace(/_/g, ' '),
        value: Number(s.total_gmv || s.total_events || 0),
        gmv: s.total_gmv,
      })),
    [serviceData]
  );

  const pieServiceTotal = useMemo(
    () => pieServiceData.reduce((acc, cur) => acc + (cur.value || 0), 0),
    [pieServiceData]
  );

  const dispatchLabel = selectedCategory === 'doctor' ? 'Clinical Duration' : 'Dispatch Response';
  const ordersLabel = selectedCategory === 'doctor' ? 'Appointments' : selectedCategory === 'medical_store' ? 'Orders' : 'Bookings';

  return (
    <InsightsPageShell
      title="Business & Vendor Monitoring"
      description="Fused business velocity, vendor operations, and technical health signals."
      info={monitoringMetricInfo.page}
      actions={
        <>
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
            {TIME_RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setTimeRange(r.value)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  timeRange === r.value
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={handleManualRefresh} disabled={refreshing} className={opsPrimaryBtn}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh Data'}
          </button>
        </>
      }
    >
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleTabChange(tab.value)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            <InsightsStatCard
              label="Net Revenue"
              value={`₹${totalRevenue.toLocaleString()}`}
              icon={DollarSign}
              accent={revenueTrendPct < -15 ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}
              loading={overviewLoading}
              info={monitoringMetricInfo.platformCommission}
              sub={pctChangeLabel(revenueTrendPct)}
              className={revenueTrendPct < -15 ? alertCardClass : ''}
            />
            <InsightsStatCard
              label="Interactions"
              value={maxActiveUsers.toLocaleString()}
              icon={Users}
              accent="text-blue-600 dark:text-blue-400"
              loading={overviewLoading}
              sub={`${currentTotalUsers.toLocaleString()} verified members`}
            />
            <InsightsStatCard
              label="Direct Orders"
              value={totalOrders.toLocaleString()}
              icon={ClipboardList}
              accent={ordersTrendPct < -15 ? 'text-red-500' : 'text-primary-600 dark:text-primary-400'}
              loading={overviewLoading}
              info={monitoringMetricInfo.ordersBookings}
              sub={pctChangeLabel(ordersTrendPct)}
              className={ordersTrendPct < -15 ? alertCardClass : ''}
            />
            <InsightsStatCard
              label="Effort Capture"
              value={`${currentMargin.toFixed(1)}%`}
              icon={Percent}
              accent="text-amber-600 dark:text-amber-400"
              loading={overviewLoading}
              info={monitoringMetricInfo.gmvVsRevenue}
              sub={`${marginChange >= 0 ? '+' : ''}${marginChange.toFixed(1)}% MoM margin shift`}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <InsightsChartCard
              title="Business Velocity Growth"
              info={monitoringMetricInfo.gmvVsRevenue}
              className="xl:col-span-2"
              heightClass="h-[360px]"
              actions={
                <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" /> GMV
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> Revenue
                  </span>
                </div>
              }
            >
              {overviewLoading ? (
                <div className="flex h-full items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : summaryData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  No business velocity data for {timeLabel.toLowerCase()}.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={summaryData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} axisLine={false} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={INSIGHTS_CHART_TOOLTIP}
                      formatter={(value: any, name: any) => [`₹${Number(value).toLocaleString()}`, name]}
                    />
                    <Area
                      type="monotone"
                      dataKey="total_gmv"
                      stroke="#cbd5e1"
                      strokeWidth={2}
                      fill="#94a3b8"
                      fillOpacity={0.12}
                      name="Total User Spending (GMV)"
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="total_revenue"
                      stroke={CHART_COLORS.primary}
                      strokeWidth={2.5}
                      fill="url(#colorRev)"
                      name="Platform Profit (Revenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </InsightsChartCard>

            <InsightsChartCard title="Net Settled GMV Distribution" heightClass="h-[360px]">
              <div className="flex h-full flex-col">
                {overviewLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
                  </div>
                ) : pieServiceData.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center text-gray-400">
                    <AlertCircle className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-700" />
                    <span className="text-sm">No service data available.</span>
                  </div>
                ) : (
                  <>
                    <div className="h-[170px] shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <RechartsTooltip
                            contentStyle={INSIGHTS_CHART_TOOLTIP}
                            formatter={(value: any) => [`₹${Number(value).toLocaleString()}`, 'GMV']}
                          />
                          <Pie data={pieServiceData} innerRadius={48} outerRadius={78} paddingAngle={4} dataKey="value" stroke="none">
                            {pieServiceData.map((_entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_PALETTE[index % PIE_PALETTE.length]} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 flex-1 space-y-2 overflow-y-auto pr-1">
                      {pieServiceData.map((s, index) => {
                        const pct = pieServiceTotal > 0 ? Math.round((s.value / pieServiceTotal) * 100) : 0;
                        const color = PIE_PALETTE[index % PIE_PALETTE.length];
                        return (
                          <div
                            key={s.name}
                            className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2 dark:border-gray-700/40 dark:bg-gray-800/30"
                          >
                            <div className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                              <span className="text-xs font-semibold capitalize text-gray-700 dark:text-gray-200">{s.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {s.gmv !== undefined && (
                                <span className="font-mono text-xs font-semibold text-gray-500 dark:text-gray-400">
                                  ₹{Number(s.gmv).toLocaleString()}
                                </span>
                              )}
                              <span
                                className="rounded-full px-2 py-0.5 text-[11px] font-bold"
                                style={{ backgroundColor: `${color}18`, color }}
                              >
                                {pct}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </InsightsChartCard>
          </div>
        </div>
      )}

      {activeTab === 'operations' && (
        <div className={opsChartCard}>
          <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <ShieldCheck className="h-4 w-4 text-primary-600" />
                Multi-Vertical Intelligence Board
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Fused view of MySQL identity truth and BigQuery predictive analytics.
              </p>
            </div>
            <select
              value={selectedCategory || 'all'}
              onChange={(e) => handleCategoryChange(e.target.value === 'all' ? null : e.target.value)}
              className={opsFilterClass}
            >
              <option value="all">Global (All Vendors)</option>
              <option value="ambulance">Ambulance Agencies</option>
              <option value="doctor">Clinics & Doctors</option>
              <option value="medical_store">Medical Stores</option>
            </select>
          </div>

          <div className={`${opsTableWrap} mt-4`}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" style={{ minWidth: '52rem' }}>
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2.5">Vertical Identity</th>
                    <th className="px-4 py-2.5 text-center">
                      <MetricInfoLabel label="Orders & Bookings" info={monitoringMetricInfo.ordersBookings} align="center" />
                    </th>
                    <th className="px-4 py-2.5 text-center">
                      <MetricInfoLabel label={dispatchLabel} info={monitoringMetricInfo.dispatchResponse} align="center" />
                    </th>
                    <th className="px-4 py-2.5 text-center">
                      <MetricInfoLabel label="Platform Comm." info={monitoringMetricInfo.platformCommission} align="center" />
                    </th>
                    <th className="px-4 py-2.5 text-right">
                      <MetricInfoLabel label="Est. Impact" info={monitoringMetricInfo.estImpact} align="right" />
                    </th>
                    <th className="px-4 py-2.5 text-center">
                      <MetricInfoLabel label="User Rating" info={monitoringMetricInfo.userRating} align="center" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vendorsLoading ? (
                    [...Array(4)].map((_, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-700/60">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="h-4 w-full animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
                        </td>
                      </tr>
                    ))
                  ) : vendorData.length > 0 ? (
                    vendorData.map((vendor) => (
                      <React.Fragment key={vendor.vendor_id}>
                        <tr
                          onClick={() => setActiveVendor(activeVendor === vendor.vendor_id ? null : vendor.vendor_id)}
                          className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-blue-50/30 dark:border-gray-700/60 dark:hover:bg-blue-950/20"
                        >
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{vendor.name}</span>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                {vendor.vertical_grade?.replace(/\[|\]|"/g, '')}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                                  #{vendor.vendor_id.substring(0, 8)}
                                </span>
                                <span className="text-[10px] text-gray-300">|</span>
                                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                  {vendor.views || 0} impressions
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-base font-bold text-blue-600 dark:text-blue-400">{vendor.orders || 0}</span>
                              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">{ordersLabel}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-base font-bold text-indigo-600 dark:text-indigo-400">
                                {(vendor.avg_response_time_sec || 0).toFixed(0)}s
                              </span>
                              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">
                                {selectedCategory === 'doctor' ? 'Avg Consult Time' : 'Avg Response Time'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                                ₹{(vendor.platform_revenue || 0).toLocaleString()}
                              </span>
                              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">Commission</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="text-base font-bold text-amber-600 dark:text-amber-400">
                                ₹{(vendor.estimated_revenue || 0).toLocaleString()}
                              </span>
                              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">Est. Impact (GMV)</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center">
                              <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-xs font-bold text-amber-600 dark:text-amber-400">
                                <Star className="h-2.5 w-2.5" fill="currentColor" stroke="none" />
                                <span>{(vendor.avg_rating || 0).toFixed(1)}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {activeVendor === vendor.vendor_id && (
                          <tr>
                            <td colSpan={6} className="bg-transparent px-2 pb-3 pt-1">
                              <div className="ml-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700/50 dark:bg-gray-900/60">
                                <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                                  {selectedCategory === 'doctor' && vendor.profile_picture && (
                                    <img
                                      src={vendor.profile_picture}
                                      alt={vendor.name}
                                      className="h-14 w-14 rounded-lg object-cover"
                                    />
                                  )}
                                  <div>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Official Contact</p>
                                    <p className="font-mono text-sm font-semibold text-gray-800 dark:text-gray-200">{vendor.phone}</p>
                                    <p className="text-sm font-semibold text-blue-600">{vendor.email}</p>
                                  </div>
                                  {selectedCategory === 'doctor' && (
                                    <div>
                                      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Professional Exp.</p>
                                      <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{vendor.experience} Years</p>
                                      <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                                        Verified Specialist
                                      </span>
                                    </div>
                                  )}
                                  <div>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Vertical Focus</p>
                                    <span className="inline-block rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
                                      {vendor.category || vendor.vertical_grade?.replace(/\[|\]|"/g, '') || 'Service'}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Location</p>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{vendor.city || 'N/A'}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {getVendorDetailsUrl(vendor) && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(getVendorDetailsUrl(vendor)!);
                                        }}
                                        className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100 dark:border-blue-800/50 dark:bg-blue-950/30 dark:text-blue-400"
                                      >
                                        Profile Details
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTabChange('intelligence');
                                        handleVendorChange(vendor.vendor_id);
                                      }}
                                      className={opsPrimaryBtn}
                                    >
                                      Analyze Vendor
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm italic text-gray-400">
                        No specialized operational data found for this vertical. Change category or refresh.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'technical' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <InsightsChartCard
              title="System Response Lag (P95)"
              info={monitoringMetricInfo.p95Latency}
              className="lg:col-span-8"
              heightClass="h-[360px]"
            >
              {overviewLoading ? (
                <div className="flex h-full items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : healthData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">No latency data available.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={healthData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(tick) => {
                        if (!tick) return '';
                        const parts = String(tick).split('-');
                        return parts.length === 3 ? `${parts[1]}/${parts[2]}` : tick;
                      }}
                      stroke="#94a3b8"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} unit="ms" />
                    <RechartsTooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                    <Area
                      type="monotone"
                      dataKey="avg_p95_latency"
                      stroke={CHART_COLORS.primary}
                      strokeWidth={2.5}
                      fill={CHART_COLORS.primary}
                      fillOpacity={0.15}
                      name="P95 Latency"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </InsightsChartCard>

            <InsightsChartCard
              title="Error Density Scan"
              info={monitoringMetricInfo.errorRatePulse}
              className="lg:col-span-4"
              heightClass="h-[360px]"
            >
              {overviewLoading ? (
                <div className="flex h-full items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : healthData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">No error data available.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={healthData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(tick) => {
                        if (!tick) return '';
                        const parts = String(tick).split('-');
                        return parts.length === 3 ? `${parts[1]}/${parts[2]}` : tick;
                      }}
                      stroke="#94a3b8"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} unit="%" />
                    <RechartsTooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                    <Bar dataKey="avg_error_rate" fill={CHART_COLORS.critical} radius={[6, 6, 0, 0]} name="Error Rate" barSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </InsightsChartCard>
          </div>

          <div className={`${opsChartCard} flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center`}>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-950/40">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Platform Vitality Stable</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Telemetry indicates low technical debt in the current API version.
                </p>
              </div>
            </div>
            <button type="button" onClick={() => navigate('/reports')} className={opsSecondaryBtn}>
              View Detailed Logs
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {activeTab === 'intelligence' && (
        <div className="space-y-4">
          <div className={opsChartCard}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Vendor Intelligence Deep Dive</h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Select a vendor to analyze their operational performance in detail.
                </p>
              </div>
              <select
                value={selectedIntelligenceVendor || ''}
                onChange={(e) => handleVendorChange(e.target.value || null)}
                disabled={fetchingVendors}
                className={opsFilterClass}
              >
                <option value="">
                  {fetchingVendors
                    ? 'Loading vendors…'
                    : selectedCategory === 'doctor'
                      ? 'Search Doctor Clinic'
                      : selectedCategory === 'medical_store'
                        ? 'Search Medical Store'
                        : 'Search Ambulance Agency'}
                </option>
                {allAmbulanceVendors.map((v) => (
                  <option key={v.vendor_id} value={v.vendor_id}>
                    {v.name} ({v.city || 'N/A'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedIntelligenceVendor ? (
            <VendorIntelligenceDetail vendorId={selectedIntelligenceVendor} />
          ) : (
            <div
              className={`${opsChartCard} flex flex-col items-center justify-center gap-3 border-dashed py-16 text-center`}
            >
              <div className="rounded-full bg-blue-50 p-4 dark:bg-blue-950/30">
                <Activity className="h-8 w-8 text-blue-500" />
              </div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white">No vendor selected</h4>
              <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
                Choose a vendor from the dropdown above to view their detailed operational analytics.
              </p>
            </div>
          )}
        </div>
      )}
    </InsightsPageShell>
  );
};

const MetricInfoLabel: React.FC<{ label: string; info: any; align: 'center' | 'right' | 'left' }> = ({
  label,
  info,
  align,
}) => (
  <span
    className={`inline-flex items-center gap-1 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : ''}`}
  >
    {label}
    <MetricInfoPopover title={label} info={info} />
  </span>
);

export default MonitoringPage;
